import React from 'react';
import { RouteHistoryEntry } from '../hooks/useRouteHistory';

const COLOR_BADGE: Record<string, string> = {
  green:  'bg-emerald-100 text-emerald-700',
  blue:   'bg-blue-100 text-blue-700',
  yellow: 'bg-amber-100 text-amber-700',
  red:    'bg-rose-100 text-rose-700',
};

interface Props {
  history: RouteHistoryEntry[];
  onSelect: (passportCode: string, destinationCode: string) => void;
  onRemove: (passportCode: string, destinationCode: string) => void;
  onClear: () => void;
}

export default function RecentRoutes({ history, onSelect, onRemove, onClear }: Props) {
  if (history.length === 0) return null;

  return (
    <div className="mt-5">
      <div className="flex items-center justify-between mb-2.5">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Recent checks</span>
        <button
          onClick={onClear}
          className="text-[11px] text-slate-400 hover:text-slate-600 font-medium transition-colors"
        >
          Clear all
        </button>
      </div>
      <div className="flex flex-wrap gap-2">
        {history.map(entry => {
          const badgeClass = COLOR_BADGE[entry.color] ?? 'bg-slate-100 text-slate-600';
          return (
            <div
              key={`${entry.passportCode}-${entry.destinationCode}`}
              className="group flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-3 py-2 hover:border-blue-300 hover:shadow-sm transition-all cursor-pointer"
              onClick={() => onSelect(entry.passportCode, entry.destinationCode)}
            >
              <img
                src={`https://flagcdn.com/20x15/${entry.passportCode.toLowerCase()}.png`}
                alt=""
                className="w-5 h-[15px] rounded-sm object-cover flex-shrink-0"
                onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
              <span className="text-xs font-bold text-slate-700">{entry.passportCode}</span>
              <svg className="w-3 h-3 text-slate-300 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
              <img
                src={`https://flagcdn.com/20x15/${entry.destinationCode.toLowerCase()}.png`}
                alt=""
                className="w-5 h-[15px] rounded-sm object-cover flex-shrink-0"
                onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
              <span className="text-xs font-bold text-slate-700">{entry.destinationCode}</span>
              <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-md ${badgeClass}`}>
                {entry.ruleName}
              </span>
              <button
                onClick={e => { e.stopPropagation(); onRemove(entry.passportCode, entry.destinationCode); }}
                className="ml-1 opacity-0 group-hover:opacity-100 text-slate-300 hover:text-slate-600 transition-all"
                aria-label="Remove"
              >
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
